
import { defineComponent } from '@vue/composition-api';
import { FCButton } from '@fc/angie-ui';
import { FormattedProduct } from '@fc/app-common/src/services/Shopify/types';
import { metaData } from '~/configuration/metaData';
import { CMSService } from '~/services/storyBlok/cmsService';
import ReviewsGrid from '~/components/storyblok/ReviewsGrid/ReviewsGrid.vue';
import PackageAboveTheFold from '~/components/v2/pdps/PackageAboveTheFold.vue';
import FaqSection from '~/components/shop/FaqSection.vue';
import storeMutations from '~/store/constants/storeMutations';
import { transformPackageProduct } from '~/utils/transformers';
import packages from '~/data/packages.js';
import fightcampBagRing from '~/data/includes/fightcamp-bag-ring.js';

export default defineComponent({
  name: 'FightCampCore',
  head: {
    ...metaData.core,
    script: [
      {
        src: 'https://static.truemed.com/widgets/product-page-widget.min.js',
        defer: true,
      },
    ],
  },
  components: {
    PackageAboveTheFold,
    ReviewsGrid,
    FaqSection,
    FCButton,
  },
  data() {
    return {
      reviews: [],
      faqs: [],
    };
  },
  mounted() {
    this.$store.commit(storeMutations.SET_MODAL_SUPPRESSION, true);

    if (window.affirm && window.affirm.ui) {
      window.affirm.ui.ready(() => {
        window.affirm.ui.refresh();
      });
    }
  },
  destroyed() {
    this.$store.commit(storeMutations.SET_MODAL_SUPPRESSION, false);
  },
  computed: {
    packageData() {
      let pkg = packages[1];
      if (this.isCanada) {
        pkg = {
          ...pkg,
          includes: [
            ...pkg.includes,
            fightcampBagRing,
          ],
          assets: [
            {
              src: 'https://a.storyblok.com/f/152150/1200x1120/9e899fdccd/core_package_ring.jpg',
              alt: 'FightCamp Core Package with Ring',
            },
            ...pkg.assets.slice(1),
          ],
        };
      }

      return transformPackageProduct({
        cmsContent: pkg,
        eCommContent: this.$store.getters.productsCollection,
      });
    },
    isCanada() {
      return this.$store.getters.isGeoLocatedCanada;
    },
    pdpUpsells() {
      const bagRingId = process.env.NUXT_ENV_IS_PROD === 'true' ? '7667402899692' : '7091668811865';
      const glovesId = process.env.NUXT_ENV_IS_PROD === 'true' ? '8448247234796' : '4252926115929';
      const hrmId = process.env.NUXT_ENV_IS_PROD === 'true' ? '7667444646124' : '7091661111385';

      // Hide bag ring for Canadian customers
      if (this.isCanada) {
        return [
          {
            id: `gid://shopify/Product/${glovesId}`,
            label: '',
          },
          {
            id: `gid://shopify/Product/${hrmId}`,
            label: '',
          },
        ];
      }

      return [
        {
          id: `gid://shopify/Product/${bagRingId}`,
          label: 'Most Popular',
        },
        {
          id: `gid://shopify/Product/${glovesId}`,
          label: '',
        },
      ];
    },
    popUpUpsells() {
      // [gloves, warranty, bag ring, hrm, jump rope]
      const devItems = ['gid://shopify/Product/4252926115929', 'gid://shopify/Product/7766283747417', 'gid://shopify/Product/7091668811865', 'gid://shopify/Product/7091660062809'];
      const prodItems = ['gid://shopify/Product/8448247234796', 'gid://shopify/Product/8588387516652', 'gid://shopify/Product/7667402899692', 'gid://shopify/Product/7667442450668'];
      const bagRingId = process.env.NUXT_ENV_IS_PROD === 'true' ? '7667402899692' : '7091668811865';

      let items = process.env.NUXT_ENV_IS_PROD === 'true' ? prodItems : devItems;

      // Filter out bag ring for Canadian customers
      if (this.isCanada) {
        items = items.filter(id => id !== `gid://shopify/Product/${bagRingId}`);
      }

      return items.map(id => {
        const prod = this.$store.getters.productsCollection?.find((item: FormattedProduct) => item.id === id);
        return {
          ...prod,
          value: prod?.variants?.[0]?.id,
          cta: 'Add to Cart',
          isDisabled: false,
        };
      });
    },
  },
  methods: {
    scrollToTop() {
      if (process.client) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    },
  },
  async asyncData({ $storyapi, error }) {
    try {
      const storyBlok = new CMSService({ cmsService: $storyapi });
      const reviews = await storyBlok.getAll('user-reviews');

      const faqs = await storyBlok.get('global/globalpackagefaqs');
      const faqList = faqs?.data?.story?.content?.global?.[0]?.container || [];

      return {
        reviews, faqs: faqList,
      };
    } catch (e: any) {
      error({ message: e.message });
      return {};
    }
  },
});
